import { WordScore } from 'utils/types'
import { Exercise } from '../Exercise'
import { LogoutAction } from '../User'

export interface BookProgress {
  _id: string
  bookId: string
  index: number
  indexListening: number
  isJourneyPage?: boolean
  werePointsAwarded: boolean
  isCompletedListening: boolean
  isCompleted: boolean
  updatedAt: number
  difficultWords: WordScore[]
  rating?: BookRate
  isFavorite?: {
    listening: boolean
    reading: boolean
  }
}

export enum BookMode {
  READ = 'read',
  LISTEN = 'listen'
}

export enum BookRate {
  GOOD = 'good',
  BAD = 'bad'
}

export enum BookType {
  Reading = 'Reading',
  Listening = 'Listening',
  Mix = 'Mix'
}

export enum CEFRLevels {
  PreA1 = 'PreA1',
  A11 = 'A1.1',
  A12 = 'A1.2',
  A13 = 'A1.3',
  A21 = 'A2.1',
  A22 = 'A2.2',
  A23 = 'A2.3',
  B11 = 'B1.1',
  B12 = 'B1.2',
  B13 = 'B1.3',
  B21 = 'B2.1',
  B22 = 'B2.2',
  B23 = 'B2.3',
  C1plus = 'C1+'
}

export enum GESELevels {
  ONE = '1',
  TWO = '2',
  THREE = '3',
  FOUR = '4',
  FIVE = '5',
  SIX = '6',
  SEVEN = '7',
  EIGHT = '8',
  NINE = '9',
  TEN = '10',
  ELEVEN = '11',
  TWELVE = '12'
}

export enum BookBranding {
  NONE = 'None',
  ASTRID_CLASSIC = 'Astrid Classic',
  WATERBEAR = 'Waterbear'
}

export interface Book {
  _id: string
  exercises: string[] // array of exercises ids
  imageUrl?: string
  imageWideUrl?: string
  title: string
  visible: boolean
  order?: number
  author: string
  illustrator: string
  publisher: string
  difficultyLevel: number
  recommended: boolean
  totalPoints: number
  cefrLevel?: CEFRLevels
  geseLevel?: GESELevels
  hashtags: string[]
  keywords: string[]
  targetCountries: string[]
  bookType: BookType
  branding: BookBranding
  hideTitle: boolean
  isPlacementTest?: boolean
}

export interface PlacementTest {
  _id: string
  level: string
  title: string
  platform: string
  totalPoints: number
  isPlacementTest: boolean
  isCompleted: boolean
  reading: Exercise[]
  building: Exercise[]
  listening: Exercise[]
}

export interface PlacementTestScore {
  _id: string
  userId: string
  placementTestId: string
  scores: { exerciseId: string; score: number }[]
}

export type BookWithProgress = {
  book: Book
  bookProgress: BookProgress | null
}

export type BookEntityByStatus = {
  entities: BookWithProgress[]
  recommendedBookIndex: number
}

export type BookEntitiesByStatus = {
  completed: BookEntityByStatus
  uncompleted: BookEntityByStatus
}

export enum BookServiceActionTypes {
  SetCurrentBookId = 'Book/set_current_book_id',
  SetBookStarted = 'Book/set_book_stared',
  AsyncGetAll = 'Book/async_get_all',
  AsyncGetPlacementTest = 'Book/async_get_placement_test',
  AsyncCreatePlacementTestScore = 'Book/async_create_placement_test_score',
  AsyncUpdatePlacementTestScore = 'Book/async_update_placement_test_score',
  AsyncGetRecommendedBook = 'Book/async_get_recommended_book',
  AsyncGetBooksProgress = 'Book/async_get_books_progress',
  AsyncCreateProgressEntity = 'Book/async_create_progress_entity',
  AsyncUpdateProgressEntity = 'Book/async_update_progress_entity'
}

export type SetCurrentBookIdAction = {
  type: BookServiceActionTypes.SetCurrentBookId
  bookId: string | null
  bookMode: BookMode | null
}

export type SetBookStaredAction = {
  type: BookServiceActionTypes.SetBookStarted
  timestamp: number | null
}

export type AsyncGetAllBooksAction = {
  type: BookServiceActionTypes.AsyncGetAll
  bookEntities: Book[]
}

export type AsyncGetBooksProgressAction = {
  type: BookServiceActionTypes.AsyncGetBooksProgress
  progressEntities: BookProgress[]
}

export type AsyncCreateProgressEntityAction = {
  type: BookServiceActionTypes.AsyncCreateProgressEntity
  progressEntity: BookProgress
}

export type AsyncUpdateProgressEntityAction = {
  type: BookServiceActionTypes.AsyncUpdateProgressEntity
  progressEntity: BookProgress
}

export type AsyncGetRecommendedBookAction = {
  type: BookServiceActionTypes.AsyncGetRecommendedBook
  book: Book
}

export type AsyncGetPlacementTestAction = {
  type: BookServiceActionTypes.AsyncGetPlacementTest
  placementTestEntity: PlacementTest
}

export type AsyncCreatePlacementTestScoreAction = {
  type: BookServiceActionTypes.AsyncCreatePlacementTestScore
  placementTestScoreEntity: PlacementTestScore
}

export type AsyncUpdatePlacementTestScoreAction = {
  type: BookServiceActionTypes.AsyncUpdatePlacementTestScore
  placementTestScoreEntity: PlacementTestScore
}

export type BookServiceActions =
  | LogoutAction
  | SetCurrentBookIdAction
  | SetBookStaredAction
  | AsyncGetAllBooksAction
  | AsyncGetBooksProgressAction
  | AsyncCreateProgressEntityAction
  | AsyncUpdateProgressEntityAction
  | AsyncGetRecommendedBookAction
  | AsyncGetPlacementTestAction
  | AsyncCreatePlacementTestScoreAction
  | AsyncUpdatePlacementTestScoreAction

export type BookServiceInitialState = {
  entities: Book[]
  lastUpdated: string | null
  bookStarted: number | null
  progressEntities: BookProgress[]
  currentBookId: string | null
  currentBookMode: BookMode | null
  placementTestEntity: PlacementTest | null
  placementTestScoreEntity: PlacementTestScore | null
}
